// src/pages/HomzReadyPages/HomzReadyEmptyStatePage.jsx
import React, { useEffect, useRef, useState } from "react";
import chairImg from "../../assets/homzready/chair.png";
import logoIcon from "../../assets/homzready/logo-icon.png";
import HomzReadyPulseLoader from "../../components/homzready/HomzReadyPulseLoader.jsx";

export default function HomzReadyEmptyStatePage() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const firstFocusable = useRef(null);

  useEffect(() => {
    if (open && firstFocusable.current) firstFocusable.current.focus();
    const onEsc = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [open]);

  const handleBrowse = () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setOpen(true);
    }, 600);
  };

  return (
    // Keep flex-col for structure but DO NOT push footer with mt-auto.
    <div className="min-h-screen w-full bg-white flex flex-col relative overflow-hidden">
      {/* Logo */}
      <div className="absolute right-6 top-6 z-20">
        <img src={logoIcon} alt="HomzReady" className="h-12 w-12 rounded-lg" />
      </div>

      {/* Content (reduced vertical padding to avoid extra white space) */}
      <main className="relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-10 items-center px-5 sm:px-8 lg:px-16 py-6 lg:py-10">
          {/* LEFT: Chair */}
          <div className="order-2 lg:order-1 flex justify-start">
            <img
              src={chairImg}
              alt="Chair"
              className="w-full max-w-[640px] lg:max-w-[760px] h-auto object-contain sm:mt-1 lg:mt-0"
            />
          </div>

          {/* RIGHT: Text */}
          <section className="order-1 lg:order-2 py-1 sm:py-2">
            <h1 className="text-5xl sm:text-6xl font-extrabold tracking-tight text-black">
              Welcome
            </h1>

            <p className="mt-4 sm:mt-5 text-gray-700 leading-relaxed max-w-md">
              Welcome to the detailed reporting system from{" "}
              <strong>HomzReady</strong>. We always strive to provide a different
              experience that makes your project smoother and more reliable.
            </p>

            <p className="mt-4 font-semibold tracking-wider text-[11px] sm:text-xs text-gray-900">
              THIS IS A BETA VERSION
            </p>

            <div className="mt-5 sm:mt-6">
              <button
                onClick={handleBrowse}
                className="inline-flex items-center rounded-full px-6 py-3 text-base font-semibold shadow-md
                           bg-teal-500 hover:bg-teal-600 active:bg-teal-700 text-white focus:outline-none
                           focus-visible:ring-4 focus-visible:ring-teal-300 transition"
              >
                Browse Your Projects
              </button>
            </div>
          </section>
        </div>
      </main>

      {/* Footer (no mt-auto → sits right after content) */}
      <footer className="w-full text-center py-4 sm:py-5 text-xs text-gray-500 bg-white relative z-10">
        © {new Date().getFullYear()} <span className="font-medium">HomzReady</span> | All Rights Reserved
      </footer>

      {/* Loader */}
      {loading && <HomzReadyPulseLoader size={88} label="Preparing your projects…" />}

      {/* Modal */}
      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="hr-modal-title"
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
        >
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <div className="relative bg-white w-full max-w-2xl rounded-2xl shadow-2xl border border-gray-100">
            <button
              ref={firstFocusable}
              onClick={() => setOpen(false)}
              aria-label="Close"
              className="absolute right-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-full
                         border border-red-200 text-red-500 hover:bg-red-50 focus:outline-none focus-visible:ring-4
                         focus-visible:ring-red-200 transition"
            >
              ×
            </button>

            <div className="px-6 sm:px-8 py-8 sm:py-10">
              <div className="flex items-start gap-4">
                <div className="shrink-0">
                  <div className="relative h-12 w-12 rounded-full bg-red-50 border border-red-200 flex items-center justify-center">
                    <span className="text-2xl">📍</span>
                    <span className="absolute -right-1 -bottom-1 text-yellow-500 text-xl">⚠️</span>
                  </div>
                </div>
                <div className="min-w-0">
                  <h2 id="hr-modal-title" className="text-lg font-semibold text-gray-900">
                    Oops! No active projects with HomzReady yet!
                  </h2>
                  <p className="mt-2 text-gray-700">We’re excited to work with you soon.</p>
                </div>
              </div>

              <div className="mt-8 flex items-center justify-center">
                <button
                  onClick={() => setOpen(false)}
                  className="inline-flex items-center rounded-full px-8 py-3 text-base font-semibold shadow-sm
                             bg-teal-500 hover:bg-teal-600 active:bg-teal-700 text-white focus:outline-none
                             focus-visible:ring-4 focus-visible:ring-teal-300 transition"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
